import { Link } from "react-router-dom";
import { Lock, Crown, Sparkles } from "lucide-react";
import { Card } from "@/components/common";
import { useAuth } from "@/context/AuthContext";

// Wraps a premium feature. If the user's plan lacks `feature`, shows an upgrade
// card instead of the children.
export function UpgradeGate({ feature, title, blurb, requiredPlan = "Pro", children }) {
  const { can, ent } = useAuth();
  if (ent === null) return children; // entitlements not loaded yet — don't flash the gate
  if (can(feature)) return children;

  return (
    <div className="max-w-2xl mx-auto py-10" data-testid={`upgrade-gate-${feature}`}>
      <Card className="p-8 text-center relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-[0.06]"
          style={{ background: "radial-gradient(circle at 50% 0%, var(--aurum), transparent 60%)" }} />
        <div className="relative">
          <div className="mx-auto h-14 w-14 rounded-2xl btn-gold flex items-center justify-center mb-4">
            <Lock size={24} className="text-black" />
          </div>
          <h2 className="text-2xl font-bold">{title}</h2>
          <p className="mt-3 text-[var(--text-sec)] max-w-md mx-auto">{blurb}</p>
          <div className="mt-6 inline-flex items-center gap-2 text-sm text-aurum font-medium">
            {requiredPlan === "Elite" ? <Crown size={16} /> : <Sparkles size={16} />}
            Available on the {requiredPlan} plan
          </div>
          <div className="mt-6">
            <Link to="/pricing" data-testid="upgrade-cta"
              className="btn-gold inline-flex rounded-full px-6 py-2.5 text-sm font-semibold hover:brightness-110 transition-all">
              Upgrade to {requiredPlan}
            </Link>
          </div>
        </div>
      </Card>
    </div>
  );
}

export function PlanBadge({ plan }) {
  const map = {
    FREE: { c: "#98A2B3", label: "Free" },
    PRO: { c: "#60A5FA", label: "Pro" },
    ELITE: { c: "#D6AE57", label: "Elite" },
  };
  const p = map[plan] || map.FREE;
  return (
    <span className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold tracking-wide"
      style={{ color: p.c, background: `${p.c}1a`, border: `1px solid ${p.c}33` }} data-testid="plan-badge">
      {plan === "ELITE" && <Crown size={10} />}{p.label.toUpperCase()}
    </span>
  );
}

export function MarketStatusBadge({ marketOpen }) {
  if (marketOpen === undefined || marketOpen === null) return null;
  const open = Boolean(marketOpen);
  const color = open ? "#21C58E" : "#98A2B3";
  return (
    <span className="hidden sm:inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-[11px] font-semibold tracking-wide"
      style={{ color, background: `${color}1a`, border: `1px solid ${color}33` }} data-testid="market-status-badge"
      title={open ? "Gold market is open" : "Gold follows forex hours — closed on weekends. Prices resume when markets reopen."}>
      <span className="h-1.5 w-1.5 rounded-full" style={{ background: color }} />
      {open ? "MARKETS OPEN" : "MARKETS CLOSED"}
    </span>
  );
}
